<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch, type Ref} from 'vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import MediaControlsOverlay from '@/vue/molecules/media-controls-overlay.vue';
import MediaControlButton from '@/vue/atoms/media-control-button.vue';
import {ReccoMediaType} from '@/ts/types/component/recco/media.type';
import {useMediaPlayer} from '@/ts/composables/stateful/use-media-player';
import {useAlert} from '@/ts/composables/pure/use-alert';
import {useLogger} from '@/ts/composables/pure/use-logger';

const props = withDefaults(defineProps<{
  src: string;
  mediaType?: ReccoMediaType;
  posterSrc?: string;
  posterAlt?: string;
  warningBeforePlaying?: string | null;
  showVolumeControl?: boolean;
  showPlaybackSpeed?: boolean;
  compact?: boolean;
  /**
   * Start the media as soon as it can play, with the sound off. Muted because every browser blocks
   * an autoplay that would make noise, and because the caller that asks for this draws the video as
   * part of a page rather than as something the reader chose to open. Honoured once per mount, and
   * not at all for a reader who asks for reduced motion.
   */
  autoplayMuted?: boolean;
}>(), {
  mediaType: ReccoMediaType.Video,
  showVolumeControl: true, // eslint-disable-line vue/no-boolean-default
});

const emit = defineEmits<{
  /**
   * Playback began. Every time it begins, not only the first: a caller that takes its own furniture
   * off the frame on this needs the other edge too, and the only signal it could pair it with —
   * `autoplayEnded` — puts the furniture back, after which `hasBeenStarted` is long true and the
   * reader's own play would have gone unannounced, leaving the copy printed over a running video.
   *
   * Raised from the element saying it is playing rather than from `play()` resolving, so a playback
   * that was asked for and never began announces nothing.
   */
  playbackStarted: [];
  /**
   * Half of the media has been played, which is what counts as having watched it. Half PLAYED, not
   * the middle reached: dragging the bar there, or to the end, plays nothing. Fired once per
   * player: whatever is played after that says nothing new about the same viewing.
   */
  halfWatched: [];
  /**
   * An autoplay nobody attended reached its end and the player is back at rest: rewound, sound
   * returned. Never for a playback the reader started or took over — that one ends where they left
   * it, so the surface is not at rest and the caller must not treat it as such.
   */
  autoplayEnded: [];
}>();

const mediaEl = ref<HTMLMediaElement>() as Ref<HTMLMediaElement | undefined>;

const {
  isPlaying,
  hasBeenStarted,
  canPlay,
  currentTime,
  played,
  duration,
  isMuted,
  playbackRate,
  formattedCurrentTime,
  formattedDuration,
  play,
  pause,
  seekTo,
  toggleMute,
  setPlaybackRate,
  requestFullscreen,
  initHls,
  startHlsLoad,
} = useMediaPlayer(mediaEl, {hlsSrc: props.src});

// Half of the duration, the fraction the product treats as watched. Measured against what the
// element says it has played, so moving the progress bar or the skip buttons across the middle
// says nothing: they move the time without showing a frame.
const WATCHED_FRACTION = 0.5;

let halfWatchedReported = false;

/**
 * Whether the reader has done anything to this player: started it, paused it, turned its sound
 * back on, moved through it, put it fullscreen or changed its speed. False for a playback that only
 * ever ran because we started it.
 *
 * The welcome video plays itself, muted, and a video that played says nothing about whether anyone
 * watched it — which is why a presentation and a watch are reported separately (IPD-3114). Half of
 * an unattended autoplay is still nobody watching, so it does not count until someone touches the
 * player; from that moment the half counts as it always has (product decision, IPD-3105).
 */
let readerTouched = false;

/** Whether the playback on screen is one we started ourselves. See the autoplay watch below. */
let autoplayStarted = false;

/**
 * Whether everything played so far came from an autoplay of ours.
 *
 * Separate from `autoplayStarted`, which the end-of-play restore clears so a playback the reader
 * goes on to start themselves is not rewound as if it were ours. That clearing must not double as
 * consent to report the autoplay watched, and it did: the watch below leaves an untouched autoplay
 * unreported rather than suppressed for good, so `played` moving again re-runs it — and the
 * restore's own `seekTo(0)` fires a `timeupdate` whose recomputed `played` covers the whole file,
 * which is past the half. Only the reader taking the player over lifts this.
 */
let autoplayOwnsPlayback = false;


// Reaching the end is deliberately NOT a second way of counting as watched. Measured in Chromium:
// dragging the progress bar to the far end while the video plays fires `ended` with 0.4 seconds of
// a 6 second file actually played, so honouring it would hand back the very gap this measure
// exists to close. Someone who skips into the second half and watches it out is therefore not
// reported until they have played half of it, which is what "watched" is defined as here.
function reportHalfIfReached(): void {
  // A live stream reports an infinite duration and metadata that has not arrived reports NaN:
  // neither has a half to reach.
  const half = duration.value * WATCHED_FRACTION;
  if (halfWatchedReported || !Number.isFinite(half) || half <= 0 || played.value < half) {
    return;
  }

  // Left deliberately unreported rather than suppressed for good: someone who takes the player over
  // later — unmuting it halfway, say — has watched it from then on.
  if (autoplayOwnsPlayback && !readerTouched) {
    return;
  }

  halfWatchedReported = true;
  emit('halfWatched');
}

watch(played, reportHalfIfReached);

function withReader<T>(action: () => T): T {
  readerTouched = true;
  const result = action();
  // Checked here as well as on `played` moving, because after a full autoplay it cannot move:
  // `played` is the element's own union of played ranges, so once that covers the file it is
  // saturated, and replaying it changes nothing. Waiting for a tick that cannot arrive left a
  // reader who watched the whole thing after an unattended autoplay unreported — the video's
  // window never closed and the metric never counted them.
  reportHalfIfReached();
  return result;
}


const isVideo = computed(() => props.mediaType === ReccoMediaType.Video);
const isAudio = computed(() => props.mediaType === ReccoMediaType.Audio);
const formattedPlaybackRate = computed(() => playbackRate.value === 1 ? '1x' : `${playbackRate.value}x`);

// Auto-hide controls after inactivity
const CONTROLS_HIDE_DELAY = 2000;
const controlsVisible = ref(true);
let hideTimer: ReturnType<typeof setTimeout> | null = null;

function showControls(): void {
  controlsVisible.value = true;
  if (hideTimer) {
    clearTimeout(hideTimer);
  }
  if (isPlaying.value) {
    hideTimer = setTimeout(() => {
      controlsVisible.value = false;
    }, CONTROLS_HIDE_DELAY);
  }
}

/**
 * Whether the tap in progress is only there to bring the controls back.
 *
 * A tap reaches the page as a synthesized `mousemove` before its `click`, so the gesture that
 * reveals the controls also presses whatever sat under the finger — and on a touch screen the
 * reader cannot see what they are aiming at until after it has happened. `pointer-events: none` on
 * the hidden controls does not help: the `mousemove` has already shown them by the time the click
 * resolves its target. So the first tap only reveals, and its click is dropped.
 *
 * Touch only. A pointer that hovers keeps the controls up by moving, so a mouse click on them was
 * always deliberate.
 */
let revealOnly = false;

function onPointerDown(event: PointerEvent): void {
  revealOnly = event.pointerType !== 'mouse' && !controlsVisible.value;
  if (revealOnly) {
    // The whole synthesized sequence, not only its click: the widest control in the bar is a
    // `q-slider`, which acts on the `mousedown` a tap synthesizes, and `stopPropagation` on the
    // click never reaches it. A reveal tap landing on the scrubber would seek — which counts as
    // the reader taking the player over, so the end-of-play restore would then decline to rewind
    // it, hand the sound back or say anything, leaving a muted video stopped mid-file.
    event.preventDefault();
    showControls();
  }
}

function onClickCapture(event: MouseEvent): void {
  if (!revealOnly) {
    return;
  }
  revealOnly = false;
  event.stopPropagation();
}

function onMouseLeave(): void {
  if (isPlaying.value) {
    controlsVisible.value = false;
    if (hideTimer) {
      clearTimeout(hideTimer);
    }
  }
}

watch(isPlaying, (playing) => {
  if (playing) {
    emit('playbackStarted');
    showControls();
  } else {
    controlsVisible.value = true;
    if (hideTimer) {
      clearTimeout(hideTimer);
    }
  }
});

const {error} = useLogger();
const {showConfirmDialog} = useAlert();

async function onFirstPlay(request?: {withoutGesture?: boolean}): Promise<void> {
  try {
    startHlsLoad();
    if (props.warningBeforePlaying) {
      await showConfirmDialog(props.warningBeforePlaying, {}, `${props.src} - ${props.warningBeforePlaying}`);
    }
    await play(request);
  } catch (err) {
    error(err as string)();
  }
}

/**
 * Puts an autoplay nobody attended back the way it would have been found: rewound, with its sound
 * returned. The muting is ours — the reader never asked for it, and every browser demands it of an
 * autoplay — so leaving it on would hand them a silent player to press play on.
 *
 * Nothing is put back for a playback the reader took over. They are looking at it: rewinding it to
 * frame 0, and telling the caller the surface is at rest so it draws its copy over the frame again,
 * would undo the choice they made. Whether they muted it by hand or not, the sound stays as they
 * left it.
 */
function restoreAfterAutoplay(): void {
  if (!autoplayStarted) {
    return;
  }
  autoplayStarted = false;
  if (readerTouched) {
    return;
  }
  seekTo(0);
  if (isMuted.value) {
    toggleMute();
  }
  emit('autoplayEnded');
}

// The element's own event rather than a clock comparison: `currentTime` reaching `duration` is a
// float equality on a number the browser rounds, and HLS reports a duration its fragments do not
// add up to, so the restore would sometimes never run and leave the video muted and unrewound.
//
// Bound through the ref rather than at mount, because the element is a dynamic `component` that
// Vue swaps when `mediaType` changes: a listener left on the discarded node never fires again, and
// the video would stay muted and unrewound with the caller never told. `use-media-player` binds
// the listeners it owns the same way.
const stopEndedWatch = watch(mediaEl, (newEl, oldEl) => {
  oldEl?.removeEventListener('ended', restoreAfterAutoplay);
  newEl?.addEventListener('ended', restoreAfterAutoplay);
}, {immediate: true});

onMounted(async () => {
  if (!mediaEl.value) {
    return;
  }
  await initHls();
});

/**
 * The autoplay, once the element says it has enough to play. Routed through `onFirstPlay` rather
 * than `play` so it takes the same path a tap does — the HLS load and the `playbackStarted` the
 * caller listens to. A blocked autoplay is not an error worth reporting: `onFirstPlay` already
 * swallows it into the logger and the poster simply stays up, which is the pre-autoplay behaviour.
 */
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

watch(
  [canPlay, (): boolean => props.autoplayMuted ?? false],
  async ([ready, wanted]) => {
    // Both, and either can arrive last. Watching readiness alone missed the case this exists for:
    // the caller withholds the request while something else owns the screen, and by the time it
    // releases it the element has long been ready, so a watch on readiness never fires again.
    //
    // Guarded with a plain flag rather than by stopping the watch: this runs immediately, which is
    // during the `watch` call itself, and a handle assigned by that same statement does not exist
    // yet at that point.
    // `hasBeenStarted` as well as our own flag: this fires on whichever condition arrives last, so
    // the caller can release its half long after the reader pressed play. Muting a running
    // playback, playing it a second time and stamping it as ours — so the end rewinds it and, with
    // `readerTouched` already true, declines to hand the sound back — is the refused-autoplay
    // damage arriving through the other door.
    if (autoplayStarted || hasBeenStarted.value || !ready || !wanted || prefersReducedMotion()) {
      return;
    }
    autoplayStarted = true;
    autoplayOwnsPlayback = true;
    const wasMuted = isMuted.value;
    if (!wasMuted) {
      toggleMute();
    }
    // Nobody asked for this one, so it must not stop what the reader is listening to until it is
    // actually playing — see `play`.
    await onFirstPlay({withoutGesture: true});

    // Muted autoplay is usually allowed, never guaranteed — a phone saving power or data refuses it
    // — and `onFirstPlay` swallows that refusal into the logger. Left as it is, the mute we applied
    // would outlive the attempt and the reader's own first play would be silent, which is not how
    // this player behaved before the autoplay existed. Nothing of ours survives a refusal: the mute
    // goes back, and the playback stops counting as one we started, so the end-of-play rewind does
    // not fire on a video they played themselves. The presentation is not reported either — that is
    // the caller's, and it only hears `playbackStarted`, which a refusal never emits. Read from
    // `hasBeenStarted`, which the composable raises only once the element's own play resolves, so
    // it does not depend on an event having landed by the time this line runs.
    if (!hasBeenStarted.value) {
      // The rule guards two tasks interleaving on a shared value; this one is written only here and
      // in the restore, which cannot run for a playback that never began.
      // eslint-disable-next-line require-atomic-updates
      autoplayStarted = false;
      autoplayOwnsPlayback = false;
      if (!wasMuted && isMuted.value) {
        toggleMute();
      }
    }
  },
  {immediate: true},
);


// Before rather than after: Vue clears the template ref as it unmounts, so from `onUnmounted` the
// element is already null and the listener is never taken off. `use-media-player` documents the
// same trap on the listeners it owns.
onBeforeUnmount(() => {
  stopEndedWatch();
  mediaEl.value?.removeEventListener('ended', restoreAfterAutoplay);
  if (hideTimer) {
    clearTimeout(hideTimer);
  }
});
</script>

<template>
  <!-- Compact bar mode -->
  <div v-if="compact" class="balm-media-player-compact">
    <audio ref="mediaEl" preload="metadata" class="sr-only"/>
    <div class="compact-top">
      <div class="compact-left">
        <media-control-button
          v-if="!isPlaying"
          display-flat
          type="play"
          @click="hasBeenStarted ? withReader(play).catch((e) => error(e as string)()) : withReader(onFirstPlay)"
        />
        <media-control-button
          v-else
          display-flat
          type="pause"
          @click="withReader(pause)"
        />
        <span class="compact-time">{{ formattedCurrentTime }} / {{ formattedDuration }}</span>
      </div>
      <div class="compact-right">
        <media-control-button
          v-if="showVolumeControl"
          display-flat
          :type="isMuted ? 'unmute' : 'mute'"
          @click="withReader(toggleMute)"
        />
        <media-control-button
          v-if="showPlaybackSpeed"
          display-flat
          type="speed"
          @click="withReader(() => setPlaybackRate(playbackRate >= 2 ? 0.5 : playbackRate + 0.25))"
        >
          {{ formattedPlaybackRate }}
        </media-control-button>
      </div>
    </div>
    <q-slider
      :model-value="currentTime"
      :max="duration || 1"
      :aria-label="$t('aria_media_progress')"
      class="compact-slider"
      @update:model-value="withReader(() => seekTo($event as number))"
    />
  </div>

  <!-- Full player mode -->
  <div
    v-else
    class="balm-media-player"
    :class="{'balm-media-player--audio': isAudio}"
    @mousemove="showControls"
    @mouseleave="onMouseLeave"
    @pointerdown="onPointerDown"
    @click.capture="onClickCapture"
  >
    <!-- Audio: always-visible poster -->
    <lazy-image
      v-if="isAudio && posterSrc"
      format="webp"
      :src="posterSrc"
      loading="eager"
      :alt="posterAlt ?? ''"
      :width="1200"
      :height="675"
      class="audio-poster"
    />

    <!-- The media element. `playsinline` because iPhone Safari otherwise plays a video fullscreen,
         and refuses to start one at all without a gesture: the muted autoplay would either seize
         the screen with nobody asking or never begin, and never beginning means the presentation is
         never reported and the attempt repeats on every visit. Ignored on `audio`. -->
    <component
      :is="isAudio ? 'audio' : 'video'"
      ref="mediaEl"
      :class="{'sr-only': isAudio, 'video-element': isVideo}"
      preload="metadata"
      playsinline
    />

    <!-- Video: poster overlay that fades on play -->
    <transition name="fade-out">
      <div
        v-if="isVideo && !hasBeenStarted && posterSrc"
        class="poster-overlay"
      >
        <img
          :src="posterSrc"
          :alt="posterAlt ?? ''"
          class="poster-image"
        />
      </div>
    </transition>

    <!-- Initial play button -->
    <transition name="fade-out">
      <div v-if="!hasBeenStarted" class="initial-play-button-overlay" @click="withReader(onFirstPlay)">
        <media-control-button type="playInitial" :disable="!canPlay"/>
      </div>
    </transition>

    <!-- Dark gradient behind controls for contrast (always on audio, optional on video) -->
    <div
      v-if="isAudio || hasBeenStarted"
      class="controls-gradient"
      :class="{'controls-hidden': !controlsVisible}"
    />

    <media-controls-overlay
      v-if="hasBeenStarted"
      :class="{'controls-hidden': !controlsVisible}"
      :current-time="currentTime"
      :media-type="mediaType"
      :is-playing="isPlaying"
      :duration="duration"
      :formatted-current-time="formattedCurrentTime"
      :formatted-duration="formattedDuration"
      :show-volume-control="showVolumeControl"
      :show-playback-speed="showPlaybackSpeed"
      :is-muted="isMuted"
      :playback-rate="playbackRate"
      @update:current-time="withReader(() => seekTo($event))"
      @click-play="withReader(play).catch((e) => error(e as string)())"
      @click-pause="withReader(pause)"
      @click-fullscreen="withReader(requestFullscreen)"
      @click-toggle-mute="withReader(toggleMute)"
      @change-playback-rate="withReader(() => setPlaybackRate($event))"
    />
  </div>
</template>

<style lang="scss" scoped>
.balm-media-player-compact {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-2);
  background-color: var(--Neutral-UI-neutral-ui-20);
  border-radius: var(--card-border-radius);
  width: 100%;

  :deep(.btn) {
    color: var(--Neutral-UI-neutral-ui-100);
  }

  :deep(.speed-label) {
    color: var(--Neutral-UI-neutral-ui-100);
  }
}

.compact-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.compact-left {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-1);
}

.compact-right {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-1);
  margin-right: calc(var(--Spacing-spacing-3) - var(--Spacing-spacing-2));
}

.compact-time {
  @include font-caption-sm;
  color: var(--Neutral-UI-neutral-ui-80);
  white-space: nowrap;
}

.compact-slider {
  width: 100%;
  padding: 0 var(--Spacing-spacing-3);

  :deep(.q-slider__thumb) {
    color: var(--Neutral-UI-neutral-ui-100);
  }

  :deep(.q-slider__track) {
    color: var(--Neutral-UI-neutral-ui-100);
  }
}

.balm-media-player {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: inherit;
  background-color: var(--Neutral-UI-neutral-ui-20);

  &--audio {
    display: flex;
    align-items: center;
    justify-content: center;
    // Audio surface is always dark for contrast with white controls
    background-color: #000;
  }
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.audio-poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.poster-overlay {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  z-index: 1;
  background-color: var(--Neutral-UI-neutral-ui-20);
}

.poster-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.initial-play-button-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.controls-gradient,
:deep(.media-controls-overlay) {
  transition: opacity 0.3s ease;
}

.controls-gradient {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 35%;
  background:
    linear-gradient(
      to top,
      rgb(0 0 0 / 0.6) 0%,
      rgb(0 0 0 / 0.3) 20%,
      rgb(0 0 0 / 0.05) 70%,
      rgb(0 0 0 / 0) 100%
    );
  pointer-events: none;
}

.controls-hidden {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-out;
}

// Transitions
.fade-out-leave-active {
  transition: opacity 0.4s ease-out;
}

.fade-out-leave-to {
  opacity: 0;
}
</style>
